import React, { useEffect, useState } from "react";
import { api, workspaceLogoUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, Loader2, Settings, ArrowLeftRight, Ban } from "lucide-react";

const ROLE_LABEL = {
  super_admin: "Super Admin", manager: "Manager", crew: "Crew",
  client_admin: "Client Admin", client_manager: "Client Manager", client_crew: "Client Crew",
};

export function WorkspaceSwitcher({ open, onOpenChange, slug, canManageSettings, onOpenSettings }) {
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/auth/my-workspaces").then((r) => setWorkspaces(r.data)).finally(() => setLoading(false));
  }, [open]);

  const switchTo = async (ws) => {
    if (ws.current) return;
    setSwitching(ws.id);
    try {
      const { data } = await api.post("/auth/switch-workspace", { workspace_id: ws.id });
      localStorage.setItem("mc_ws_token", data.token);
      window.location.href = `/${data.slug}`;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't switch workspace");
      setSwitching(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C] max-w-md" data-testid="workspace-switcher">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ArrowLeftRight className="h-4 w-4 text-[#723FFF]" /> Switch workspace</DialogTitle></DialogHeader>
        <div className="py-2">
          {loading ? (
            <div className="py-8 flex justify-center text-[#9FA1A4]"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : (
            <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
              {workspaces.map((ws) => {
                const suspended = ws.status === "suspended";
                return (
                  <button
                    key={ws.id}
                    data-testid="switcher-workspace-item"
                    onClick={() => switchTo(ws)}
                    disabled={ws.current || suspended || switching}
                    className={`w-full flex items-center gap-3 rounded-2xl border px-3 py-2.5 text-left transition-all ${ws.current ? "border-[#723FFF] bg-[#EDE7FF]" : "border-[#EEEFEF] bg-white hover:border-[#d3d4da] hover:bg-[#F6F6FD]"} ${suspended ? "opacity-50" : ""}`}
                  >
                    {workspaceLogoUrl(ws) ? (
                      <img src={workspaceLogoUrl(ws)} alt="" className="h-10 w-10 rounded-xl object-cover border border-[#EEEFEF] shrink-0" />
                    ) : (
                      <div className="h-10 w-10 rounded-xl flex items-center justify-center font-display font-semibold text-[#06060C] shrink-0" style={{ backgroundColor: ws.accent_color || "#EDE7FF" }}>{ws.name.charAt(0)}</div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-[#06060C] truncate">{ws.name}</p>
                      <p className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">{ROLE_LABEL[ws.role] || ws.role}</p>
                    </div>
                    {switching === ws.id ? <Loader2 className="h-4 w-4 animate-spin text-[#723FFF]" />
                      : ws.current ? <Check className="h-4 w-4 text-[#723FFF]" />
                      : suspended ? <Ban className="h-4 w-4 text-[#c4c5cc]" /> : null}
                  </button>
                );
              })}
            </div>
          )}

          {canManageSettings && (
            <>
              <div className="my-3 border-t border-[#f0f0f3]" />
              <button
                data-testid="switcher-settings"
                onClick={() => { onOpenChange(false); onOpenSettings(); }}
                className="w-full flex items-center gap-3 rounded-2xl border border-[#EEEFEF] bg-white px-3 py-2.5 text-left hover:border-[#d3d4da] hover:bg-[#F6F6FD] transition-all"
              >
                <div className="h-10 w-10 rounded-xl bg-[#F6F6FD] border border-[#f0f0f3] flex items-center justify-center shrink-0"><Settings className="h-4 w-4 text-[#595D62]" /></div>
                <div className="min-w-0 flex-1"><p className="font-medium text-[#06060C]">Workspace settings</p><p className="text-xs text-[#9FA1A4]">Users, branding & access</p></div>
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
